import { useMutation } from "@tanstack/react-query";
import { isArrayWithItems, ServiceHelper } from "../../helpers";
import { arrayBufferToBase64 } from "../../helpers/file-helper";

type TAttachedFile = {
  fileName: string;
  content: string;
  contentType: string;
};

type TCompanyFormRequest = Omit<TPostCustomerServiceCompanyServiceNowParams, "attachedFiles"> & {
  attachedFiles?: Array<TAttachedFile>;
};

type TConsumerFormRequest = Omit<TPostCustomerServiceCompanyServiceNowParams, "attachedFiles"> & {
  attachedFiles?: Array<TAttachedFile>;
};

const convertFilesToAttachedFiles = async (files: Array<File> | undefined): Promise<Array<TAttachedFile>> => {
  let attachedFiles: Array<TAttachedFile> | undefined = undefined;
  if (isArrayWithItems(files)) {
    attachedFiles = await Promise.all(
      files.map(async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const base64Content = arrayBufferToBase64(arrayBuffer);

        return {
          fileName: file.name,
          content: base64Content,
          contentType: file.type,
        } satisfies TAttachedFile;
      }),
    );
  }

  return attachedFiles ? attachedFiles : [];
};

export type TPostCustomerServiceCompanyServiceNowParams = {
  requestType: string;
  subCategory: string;
  customerNumber?: string;
  invoiceNumber?: string;
  cardListType?: string;
  organisationNumber?: string;
  subject?: string;
  description?: string;
  firstName: string;
  lastName: string;
  cardNumber?: string;
  phone: string;
  email: string;
  termsAccepted: boolean;
  attachedFiles?: Array<File>;
};

export const usePostCustomerServiceCompanyServiceNow = () => {
  return useMutation({
    mutationKey: ["post-customer-service-company-service-now"],
    mutationFn: async (params: TPostCustomerServiceCompanyServiceNowParams) => {
      const attachedFiles = await convertFilesToAttachedFiles(params.attachedFiles);
      const request = {
        ...params,
        attachedFiles: attachedFiles,
      } satisfies TCompanyFormRequest;
      const bodyJson = JSON.stringify(request);

      return ServiceHelper.httpPost("/v1/servicenow/business-customer/case", {
        body: bodyJson,
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    throwOnError: false,
    retry: false,
  });
};

export type TPostCustomerServiceConsumerServiceNowParams = {
  requestType: string;
  subCategory: string;
  subject: string;
  description: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  station: string;
  date: string;
  cardNumber: string;
  amount: string;
  termsAccepted: boolean;
  attachedFiles?: Array<File>;
};

export const usePostCustomerServiceConsumerServiceNow = () => {
  return useMutation({
    mutationKey: ["post-customer-service-consumer-service-now"],
    mutationFn: async (params: TPostCustomerServiceConsumerServiceNowParams) => {
      const attachedFiles = await convertFilesToAttachedFiles(params.attachedFiles);
      const request = {
        ...params,
        attachedFiles: attachedFiles,
      } satisfies TConsumerFormRequest;
      const bodyJson = JSON.stringify(request);
      return ServiceHelper.httpPost("/v1/servicenow/private-customer/case", {
        body: bodyJson,
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    throwOnError: false,
    retry: false,
  });
};

export type TPostEdiInvoicServiceNowParams = {
  requestType: string;
  firstName?: string;
  lastName?: string;
  orgno?: string;
  customerNumber?: string;
  efakturaGlnOrg?: string;
  operator?: string;
  email?: string;
  phone?: string;
};

export const usePostEdiInvoiceServiceNow = () => {
  return useMutation({
    mutationKey: ["post-edi-invoice"],
    mutationFn: async (params: TPostEdiInvoicServiceNowParams) => {
      const bodyString = JSON.stringify(params);
      return ServiceHelper.httpPost("/v1/servicenow/edi-invoice/case", {
        body: bodyString,
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    throwOnError: false,
    retry: false,
  });
};

export type TPostComplaintServiceNowParams = {
  requestType: string;
  subject?: string;
  customerNumber?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  email?: string;
  phone?: string;
  date?: string;
  location?: string;
  description?: string;
  termsAccepted: boolean;
};

export const usePostComplaintServiceNow = () => {
  return useMutation({
    mutationKey: ["post-complaint"],
    mutationFn: async (params: TPostComplaintServiceNowParams) => {
      const bodyString = JSON.stringify(params);
      return ServiceHelper.httpPost("/v1/servicenow/complaint/case", {
        body: bodyString,
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    throwOnError: false,
    retry: false,
  });
};
